/**
 * Three-way document merge.
 *
 * Turns "another tab saved first" from a dead end into a rebase: given the last
 * document storage acknowledged (base), the local document (ours) and what
 * storage holds now (theirs), produce one document containing both sides' work.
 * Deterministic and side-based — no timestamps, so two tabs' clocks cannot
 * corrupt anything. Ties go to THEIRS: that side is already stored, and every
 * tab running the same rule converges.
 *
 * The output always satisfies every `validateDoc` rule (positions 0..n-1,
 * seats.length === seatCount, one chair per guest, no unknown refs, per-shape
 * caps) — `saveLayout` re-validates regardless, so a bug here degrades to a
 * refused save, never to a broken stored plan.
 */

import { isEventKind } from './events'
import { maxSeatCount } from './geometry'
import type { EventKind, Guest, Plan, PlanSettings, Seat, Table } from './types'

/** The document, as merge and undo both see it. The camera is not part of it. */
export interface PlanDoc {
  title: string
  eventKind: EventKind
  eventDate: string | null
  venue: string | null
  settings: PlanSettings
  tables: Table[]
  guests: Guest[]
}

const clone = <T,>(value: T): T =>
  typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value))

export function docOf(plan: Plan | PlanDoc): PlanDoc {
  return {
    title: plan.title,
    eventKind: plan.eventKind,
    eventDate: plan.eventDate,
    venue: plan.venue,
    settings: clone(plan.settings),
    tables: clone(plan.tables),
    guests: clone(plan.guests),
  }
}

/** Value equality for plain JSON-shaped data. */
export function eq(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, index) => eq(item, b[index]))
  }
  const ka = Object.keys(a as object)
  const kb = Object.keys(b as object)
  if (ka.length !== kb.length) return false
  return ka.every((key) => eq((a as never)[key], (b as never)[key]))
}

export const docsEqual = (a: PlanDoc, b: PlanDoc): boolean => eq(a, b)

/** ours if only we changed it; theirs if they changed it (ties → theirs). */
function pick3<T>(base: T, ours: T, theirs: T): T {
  if (eq(theirs, base)) return clone(ours)
  return clone(theirs)
}

interface Assignment {
  tableId: string
  seatId: string
}

/** Who sits where, as one guest→chair map rather than per-seat fields. */
function assignments(doc: PlanDoc): Map<string, Assignment> {
  const map = new Map<string, Assignment>()
  for (const table of doc.tables) {
    for (const seat of table.seats) {
      if (seat.guestId) map.set(seat.guestId, { tableId: table.id, seatId: seat.id })
    }
  }
  return map
}

function byId<T extends { id: string }>(items: T[]): Map<string, T> {
  return new Map(items.map((item) => [item.id, item]))
}

/**
 * Presence merge shared by guests and tables: additions kept, a deletion wins
 * only over an *untouched* entity — edit beats delete, because an extra row is
 * one click to remove and a destroyed one is unrecoverable. Order is theirs
 * first (the stored order), then our own additions.
 */
function mergeCollection<T extends { id: string }>(
  base: T[],
  ours: T[],
  theirs: T[],
  mergeOne: (b: T | undefined, o: T, t: T) => T,
): T[] {
  const baseMap = byId(base)
  const oursMap = byId(ours)
  const theirsMap = byId(theirs)
  const out: T[] = []

  for (const theirItem of theirs) {
    const ourItem = oursMap.get(theirItem.id)
    const baseItem = baseMap.get(theirItem.id)
    if (ourItem !== undefined) {
      out.push(mergeOne(baseItem, ourItem, theirItem))
    } else if (baseItem === undefined) {
      out.push(clone(theirItem)) // their addition
    } else if (!eq(theirItem, baseItem)) {
      out.push(clone(theirItem)) // we deleted, they edited → edit wins
    } // else: our clean delete wins — drop it
  }
  for (const ourItem of ours) {
    if (theirsMap.has(ourItem.id)) continue
    const baseItem = baseMap.get(ourItem.id)
    if (baseItem === undefined) {
      out.push(clone(ourItem)) // our addition
    } else if (!eq(ourItem, baseItem)) {
      out.push(clone(ourItem)) // they deleted, we edited → edit wins
    } // else: their clean delete wins — drop it
  }
  return out
}

function mergeGuest(base: Guest | undefined, ours: Guest, theirs: Guest): Guest {
  // Same id minted on both sides is impossible with randomUUID; be deterministic.
  if (base === undefined) return clone(theirs)
  return {
    id: ours.id,
    fullName: pick3(base.fullName, ours.fullName, theirs.fullName),
    groupLabel: pick3(base.groupLabel, ours.groupLabel, theirs.groupLabel),
    note: pick3(base.note, ours.note, theirs.note),
    rsvp: pick3(base.rsvp, ours.rsvp, theirs.rsvp),
    photoAssetId: pick3(base.photoAssetId, ours.photoAssetId, theirs.photoAssetId),
    photoUrl: pick3(base.photoUrl, ours.photoUrl, theirs.photoUrl),
  }
}

interface Structure {
  shape: Table['shape']
  seatCount: number
  seatIds: string[]
}

const structureOf = (table: Table): Structure => ({
  shape: table.shape,
  seatCount: table.seatCount,
  seatIds: table.seats.map((seat) => seat.id),
})

/** Chairs stripped of their guests — the assignment engine re-seats everyone. */
const bareSeats = (table: Table): Table => ({
  ...table,
  seats: table.seats.map((seat, index) => ({ ...seat, position: index, guestId: null })),
})

/**
 * Chairs are one unit: interleaving two seat arrays breaks the 0..n-1 rule, so
 * the whole (shape, seatCount, seats) block comes from exactly one side — ours
 * if only we restructured, otherwise theirs. Position is one fact too, so x and
 * y move as a pair rather than crossing into a corner neither side chose.
 */
function mergeTable(base: Table | undefined, ours: Table, theirs: Table): Table {
  if (base === undefined) return bareSeats(clone(theirs))
  const oursChanged = !eq(structureOf(ours), structureOf(base))
  const theirsChanged = !eq(structureOf(theirs), structureOf(base))
  const donor = theirsChanged || !oursChanged ? theirs : ours

  const position = pick3(
    { x: base.x, y: base.y },
    { x: ours.x, y: ours.y },
    { x: theirs.x, y: theirs.y },
  )
  return {
    id: ours.id,
    name: pick3(base.name, ours.name, theirs.name),
    color: pick3(base.color, ours.color, theirs.color),
    rotation: pick3(base.rotation, ours.rotation, theirs.rotation),
    zIndex: pick3(base.zIndex, ours.zIndex, theirs.zIndex),
    x: position.x,
    y: position.y,
    shape: donor.shape,
    seatCount: donor.seatCount,
    seats: donor.seats.map((seat, index) => ({
      id: seat.id,
      position: index,
      guestId: null,
      label: seat.label,
    })),
  }
}

export function mergeDocs(base: PlanDoc, ours: PlanDoc, theirs: PlanDoc): PlanDoc {
  const guests = mergeCollection(base.guests, ours.guests, theirs.guests, mergeGuest)
  // Strip assignments on EVERY path — including tables arriving through the
  // add/edit-beats-delete branches as raw clones — so the engine below is the
  // single authority on who sits where. Without this a raw-cloned table could
  // double-seat a guest who is also placed by the map.
  const tables = mergeCollection(base.tables, ours.tables, theirs.tables, mergeTable).map(
    bareSeats,
  )

  // --- re-seat everyone ------------------------------------------------------
  const baseAsg = assignments(base)
  const oursAsg = assignments(ours)
  const theirsAsg = assignments(theirs)
  const seatById = new Map<string, Seat>()
  for (const table of tables) for (const seat of table.seats) seatById.set(seat.id, seat)

  const sameSeat = (a?: Assignment, b?: Assignment) => a?.seatId === b?.seatId
  const fromTheirs: Array<[string, Assignment | undefined]> = []
  const fromOurs: Array<[string, Assignment | undefined]> = []
  for (const guest of guests) {
    const b = baseAsg.get(guest.id)
    const o = oursAsg.get(guest.id)
    const t = theirsAsg.get(guest.id)
    if (sameSeat(o, b) || !sameSeat(t, b)) fromTheirs.push([guest.id, t])
    else fromOurs.push([guest.id, o])
  }
  // Theirs-sourced placements land first, so on contention theirs keeps the
  // chair and the ours-sourced guest visibly returns to the sidebar — never a
  // silent double-booking, and never a guest quietly deleted.
  for (const [guestId, target] of [...fromTheirs, ...fromOurs]) {
    if (!target) continue
    const seat = seatById.get(target.seatId)
    if (seat && seat.guestId === null) seat.guestId = guestId
  }

  // --- defensive normalisation ----------------------------------------------
  for (const table of tables) {
    const ceiling = maxSeatCount(table.shape)
    while (table.seats.length > ceiling) {
      const emptyIndex = table.seats.map((seat) => seat.guestId).lastIndexOf(null)
      table.seats.splice(emptyIndex >= 0 ? emptyIndex : table.seats.length - 1, 1)
    }
    table.seats = table.seats.map((seat, index) =>
      seat.position === index ? seat : { ...seat, position: index },
    )
    table.seatCount = table.seats.length
  }

  return {
    title: pick3(base.title, ours.title, theirs.title),
    eventKind: pick3(base.eventKind, ours.eventKind, theirs.eventKind),
    eventDate: pick3(base.eventDate, ours.eventDate, theirs.eventDate),
    venue: pick3(base.venue, ours.venue, theirs.venue),
    settings: {
      defaultSeats: {
        round: pick3(
          base.settings.defaultSeats.round,
          ours.settings.defaultSeats.round,
          theirs.settings.defaultSeats.round,
        ),
        square: pick3(
          base.settings.defaultSeats.square,
          ours.settings.defaultSeats.square,
          theirs.settings.defaultSeats.square,
        ),
        rectangle: pick3(
          base.settings.defaultSeats.rectangle,
          ours.settings.defaultSeats.rectangle,
          theirs.settings.defaultSeats.rectangle,
        ),
        sweetheart: pick3(
          base.settings.defaultSeats.sweetheart,
          ours.settings.defaultSeats.sweetheart,
          theirs.settings.defaultSeats.sweetheart,
        ),
      },
      defaultTableColor: pick3(
        base.settings.defaultTableColor,
        ours.settings.defaultTableColor,
        theirs.settings.defaultTableColor,
      ),
      seatLabels: pick3(
        base.settings.seatLabels,
        ours.settings.seatLabels,
        theirs.settings.seatLabels,
      ),
    },
    tables,
    guests,
  }
}

/** The referential rules a save must pass (the port of `PlanLayoutIn`'s). `[]` means it would be stored. */
export function validateDoc(doc: PlanDoc): string[] {
  const problems: string[] = []
  if (!isEventKind(doc.eventKind)) problems.push(`unknown event kind ${String(doc.eventKind)}`)
  const tableIds = new Set<string>()
  const seatIds = new Set<string>()
  const guestIds = new Set(doc.guests.map((guest) => guest.id))
  if (guestIds.size !== doc.guests.length) problems.push('duplicate guest ids')
  const seated = new Set<string>()

  for (const table of doc.tables) {
    if (tableIds.has(table.id)) problems.push(`duplicate table id ${table.id}`)
    tableIds.add(table.id)
    if (table.seats.length !== table.seatCount) {
      problems.push(
        `table ${table.id}: seatCount ${table.seatCount} != ${table.seats.length} seats`,
      )
    }
    if (table.seatCount > maxSeatCount(table.shape)) {
      problems.push(`table ${table.id}: over the ${table.shape} ceiling`)
    }
    table.seats.forEach((seat, index) => {
      if (seat.position !== index) problems.push(`table ${table.id}: positions not 0..n-1`)
      if (seatIds.has(seat.id)) problems.push(`duplicate seat id ${seat.id}`)
      seatIds.add(seat.id)
      if (seat.guestId) {
        if (!guestIds.has(seat.guestId)) problems.push(`seat ${seat.id}: unknown guest`)
        if (seated.has(seat.guestId)) problems.push(`guest ${seat.guestId} in two chairs`)
        seated.add(seat.guestId)
      }
    })
  }
  return problems
}
